import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import {
    buildCredentialUsageSelectionKey,
} from '@/lib/secrets/credentialUsageSelection';
import type {
    CredentialUsageListItem,
    CredentialUsageReference,
} from '@pillar-portal/api-types';

export interface CredentialDeleteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    credentialLabel: string;
    usages: ReadonlyArray<CredentialUsageListItem>;
    isLoadingUsages: boolean;
    usageLoadError: unknown;
    selectedUsageKeys: ReadonlyArray<string>;
    onToggleUsageSelection: (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ) => void;
    onToggleSelectAll: (checked: boolean) => void;
    onConfirm: () => void;
    onRemove: () => void;
    isDeleting: boolean;
    isRemoving: boolean;
}

export function buildCredentialUsageDetailHref(
    usage: CredentialUsageListItem
): string {
    if (usage.usage_type === 'logical_terminal') {
        return `/payments-infrastructure/logical-terminals/${usage.logical_terminal_uuid}`;
    }
    return `/payments-infrastructure/terminal-devices/${usage.terminal_device_uuid}`;
}

export function CredentialDeleteDialog({
    open,
    onOpenChange,
    credentialLabel,
    usages,
    isLoadingUsages,
    usageLoadError,
    selectedUsageKeys,
    onToggleUsageSelection,
    onToggleSelectAll,
    onConfirm,
    onRemove,
    isDeleting,
    isRemoving,
}: CredentialDeleteDialogProps) {
    const isDeleteBlockedByUsage: boolean = usages.length > 0;
    const areAllUsagesSelected: boolean =
        usages.length > 0 && selectedUsageKeys.length === usages.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Credential</DialogTitle>
                    <DialogDescription>
                        {isLoadingUsages
                            ? 'Checking whether this credential is currently in use.'
                            : isDeleteBlockedByUsage
                              ? 'This credential cannot be deleted because it is still used by the following resources.'
                              : `Are you sure you want to delete the secret for ${credentialLabel}?`}
                    </DialogDescription>
                </DialogHeader>
                {usageLoadError ? (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {usageLoadError instanceof Error
                                ? usageLoadError.message
                                : 'Failed to load credential usages'}
                        </AlertDescription>
                    </Alert>
                ) : isDeleteBlockedByUsage ? (
                    <div className="space-y-3">
                        <Alert variant="destructive">
                            <AlertDescription>
                                Remove this credential from the resources below.
                            </AlertDescription>
                        </Alert>
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                                checked={areAllUsagesSelected}
                                onCheckedChange={(checked: boolean) =>
                                    onToggleSelectAll(checked)
                                }
                            />
                            <span>Select all</span>
                        </label>
                        <div className="space-y-2">
                            {usages.map((usage) => {
                                const usageSelectionKey: string =
                                    buildCredentialUsageSelectionKey(usage);
                                const isSelected: boolean =
                                    selectedUsageKeys.includes(usageSelectionKey);
                                return (
                                    <label
                                        className="flex items-start gap-3 rounded border p-3 cursor-pointer"
                                        key={usageSelectionKey}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked: boolean) =>
                                                onToggleUsageSelection(usage, checked)
                                            }
                                        />
                                        <Link
                                            className="text-primary underline underline-offset-2"
                                            to={buildCredentialUsageDetailHref(usage)}
                                        >
                                            {usage.display_name}
                                        </Link>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isDeleting || isRemoving}
                    >
                        Cancel
                    </Button>
                    {isDeleteBlockedByUsage ? (
                        <Button
                            onClick={onRemove}
                            disabled={
                                selectedUsageKeys.length === 0 || isRemoving
                            }
                        >
                            {isRemoving ? 'Removing...' : 'Remove'}
                        </Button>
                    ) : (
                        <Button
                            onClick={onConfirm}
                            disabled={isLoadingUsages || isDeleting}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
